import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Gives custom dialogs the focus behavior aria-modal promises: focus enters
 * on open, Tab/Shift+Tab stay inside, and focus returns to the opener on close.
 */
export function useDialogFocus<T extends HTMLElement = HTMLDivElement>(
  active = true,
  restoreFocusTo?: HTMLElement | null,
) {
  const dialogRef = useRef<T>(null);

  useEffect(() => {
    if (!active) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previous =
      restoreFocusTo ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    const focusable = () =>
      Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getAttribute('aria-hidden') !== 'true',
      );

    const frame = requestAnimationFrame(() => {
      (focusable()[0] ?? dialog).focus();
    });

    const trapTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !dialog.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !dialog.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', trapTab);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', trapTab);
      if (previous?.isConnected) previous.focus();
    };
  }, [active, restoreFocusTo]);

  return dialogRef;
}
